const pageNameMap = {
  "/register": "Registration Page",
  "/user/register": "Registration Page",
  "/email-verified": "Post Registration Notification Page",
  "/user/email-verified": "Post Registration Notification Page",
  "/activate": "Activate Page",
  "/login": "Login Page",
  "/user/login": "Login Page",
  "/forgot": "Forgot Password Page",
  "/user/forgot": "Forgot Password Page",
  "/user/bankperformancemetrics": "Bank Performance Metrics Page",
  "/ewiChatBot/ewi": "Insights Page",
  "/ewiChatBot/aria": "ARIA Page",
  "/drilldown/graph": "Cluster Page",
  "/drilldown/list/": "View Details Page",
  "/bank/health-analysis/history": "History Page",
  "/cohort-management": "Cohort Management",
  "/bank/campaign-management/email-campaigns": "Email Campaign Page",
  "/bank/campaign-management/create-campaign": "Create Campaign Page",
  "/bank/campaign-management/email-templates": "Email Templates Page",
  "/bank/campaign-management/create-template": "Create Template Page",
  "/bank/campaign-management/edit-campaign": "Edit Campaign Page",
  "/bank/campaign-management/update-email-template": "Template Editor Page",
  "/bank/campaign-management/review-email": "Review Campaign Page",
  "/bank/campaign-management/consumers-list": "Consumers List Page",
  "/bank/user-management/user-management-list": "User Management Page",
  "/pages/faq": "Support Page",
  "/consultation": "Consultation Page",
  "/user/clearsession": "Clear Session Page",
  "/other-user": "Other User Page",
  "/user/other-user": "Other User Page",
  "/reset": "Reset Password Page",
  "/user/reset": "Reset Password Page",
  "/user/pricingTable": "Pricing Page",
  "/user/nofhsdashboard": "No FHS Dashboard Page",
  "/pages/settings": "Settings Page",
  "/unauthorized": "Unauthorized Page",
  "/bank/cohort-management": "Cohort Management",
  "/bank/campaign-management/SMS_campaigns": "SMS Campaign Page",
  "/bank/campaign-management/sms-campaigns/:campaignId":
    "SMS Campaign Details Page",
  "/bank/campaign-management/create-sms-campaign": "Create SMS Campaign Page",
  "/bank/campaign-management/edit-sms-campaign": "Edit SMS Campaign Page",
  "/bank/campaign-management/review-sms-campaign": "Review SMS Campaign Page",
  "/bank/campaign-management/campaign-management-locked":
    "Campaign Management Locked Page",
  "/bank/campaign-management/email-campaigns/:campaignId":
    "Email Campaign Details Page",
  "/bank/campaign-management/consumers-list-details":
    "Consumers List Details Page",
  "/bank/campaign-management/update-template": "Update Template Page",
  "/bank/payment-history/subscription-details": "Subscription Details Page",
  "/bank/payment-history/manage-subscription": "Manage Subscription Page",
};

const REPORT_SUB_PAGES = [
  ["/financial-health-score", "Analyst Dashboard Page"],
  ["/drilldown/graph", "Cluster Page"],
  ["/drilldown/list", "View Details Page"],
  ["/ewichatbot/aria", "ARIA Page"],
  ["/ewichatbot/ewi", "Insights Page"],
  ["/top_gainers", "Top Gainers Page"],
  ["/top_merchant_detailed_view", "Top Merchant Details Page"],
  ["/consumer-behavior", "Consumer Behavior Details Page"],
];

const toSegments = (path) =>
  path
    .toLowerCase()
    .split("/")
    .filter(Boolean);

// Matches the route exactly or as a prefix; `:param` segments match any value.
const routeMatches = (route, pathSegments) => {
  const routeSegments = toSegments(route);
  return (
    pathSegments.length >= routeSegments.length &&
    routeSegments.every(
      (segment, index) =>
        segment.startsWith(":") || segment === pathSegments[index],
    )
  );
};

export const resolvePageName = (currentPath) => {
  if (!currentPath) {
    return null;
  }

  const normalizedPath = currentPath.split("?")[0];

  if (normalizedPath.includes("/bank/health-analysis/report/")) {
    const reportSubPath = `${normalizedPath
      .replace("/bank/health-analysis/report", "")
      .toLowerCase()}/`;
    const subPage = REPORT_SUB_PAGES.find(([segment]) =>
      reportSubPath.includes(`${segment}/`),
    );

    return subPage?.[1] || "Dashboard Page";
  }

  const pathSegments = toSegments(normalizedPath);
  const match = Object.entries(pageNameMap)
    .filter(([route]) => routeMatches(route, pathSegments))
    .sort((left, right) => right[0].length - left[0].length)[0];

  return match?.[1] || null;
};

const toTitleWords = (segment) =>
  segment
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

// GA4 page title for a route. Unmapped routes get a stable title derived from
// the sanitized path rather than the shared document title, so every route
// stays distinct in page, path, and engagement reports.
export const resolvePageTitle = (currentPath) => {
  if (!currentPath) return null;

  const mapped = resolvePageName(currentPath);
  if (mapped) return mapped;

  const staticSegments = sanitizePagePath(currentPath)
    .split("/")
    .filter((segment) => segment && !segment.startsWith(":"));
  const lastSegment = staticSegments.at(-1);

  return lastSegment ? `${toTitleWords(lastSegment)} Page` : "Home Page";
};

const ATTRIBUTION_QUERY_PARAMS = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_id",
  "utm_content",
  "gclid",
  "dclid",
  "gbraid",
  "wbraid",
]);

const isEmailAddress = (value) => {
  const atIndex = value.indexOf("@");
  if (atIndex <= 0 || atIndex !== value.lastIndexOf("@")) {
    return false;
  }

  const domainDotIndex = value.indexOf(".", atIndex + 1);
  return (
    domainDotIndex > atIndex + 1 &&
    domainDotIndex < value.length - 1 &&
    !Array.from(value).some((character) => character.trim() === "")
  );
};

export const sanitizePagePath = (pathname = "") => {
  let safePath = String(pathname).split(/[?#]/, 1)[0] || "/";

  safePath = safePath
    .replace(/^(\/(?:user\/)?(?:login|reset))\/[^/]+/i, "$1/:key")
    .replace(/^(\/bank\/health-analysis\/report)\/[^/]+/i, "$1/:id")
    .replace(
      /^(\/bank\/campaign-management\/(?:edit-campaign|review-email|consumers-list|consumers-list-details|update-email-template|update-template|email-campaigns|sms-campaigns|edit-sms-campaign|review-sms-campaign))((?:\/[^/]+)+)/i,
      (_, route, idSegments) =>
        `${route}${idSegments.replace(/\/[^/]+/g, "/:id")}`,
    )
    .replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,}(?=\/|$)/gi, "/:id")
    .replace(/\/\d{5,}(?=\/|$)/g, "/:id");

  return safePath;
};

export const sanitizePageLocation = (locationValue) => {
  const fallbackOrigin = globalThis?.location?.origin || "";
  const fallbackPath = sanitizePagePath(globalThis?.location?.pathname);

  if (!locationValue) {
    return fallbackOrigin ? `${fallbackOrigin}${fallbackPath}` : fallbackPath;
  }

  try {
    const url = new URL(locationValue, fallbackOrigin || undefined);
    const safePath = sanitizePagePath(url.pathname);
    const safeParams = [...url.searchParams.entries()].filter(
      ([key, value]) => {
        return (
          ATTRIBUTION_QUERY_PARAMS.has(key.toLowerCase()) &&
          value.length <= 100 &&
          !/[\r\n]/.test(value) &&
          !isEmailAddress(value)
        );
      },
    );
    const safeQuery = new URLSearchParams(safeParams).toString();
    const origin = url.origin === "null" ? "" : url.origin;
    const querySuffix = safeQuery ? `?${safeQuery}` : "";

    return `${origin}${safePath}${querySuffix}`;
  } catch {
    return fallbackOrigin ? `${fallbackOrigin}${fallbackPath}` : fallbackPath;
  }
};

export const resolvePageCategory = (currentPath = "") => {
  const pathname = String(currentPath).split(/[?#]/, 1)[0].toLowerCase();

  if (
    /\/(login|register|forgot|reset|activate|email-verified)(\/|$)/.test(
      pathname,
    )
  ) {
    return "authentication";
  }
  if (
    pathname.startsWith("/bank/health-analysis/report") ||
    pathname === "/user/bankperformancemetrics"
  ) {
    return "dashboard";
  }
  if (pathname.startsWith("/bank/campaign-management")) {
    return "campaign_management";
  }
  if (pathname.startsWith("/bank/user-management")) {
    return "user_management";
  }
  if (pathname.startsWith("/pages/faq")) {
    return "support";
  }

  return "application";
};
